import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Mail, 
  LayoutDashboard, 
  Inbox, 
  Clock, 
  ListTodo, 
  BarChart3, 
  Settings,
  Menu,
  X,
  LogOut,
  User,
  CreditCard,
  Sun,
  Moon,
  ChevronLeft,
  FolderKanban
} from "lucide-react";
import { useAuth, useTheme } from "../App";

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Detect the current space (Mail vs Projects) from URL.
  // Default to "mail" so unknown sub-routes still get a usable sidebar.
  const space = location.pathname.startsWith("/dashboard/projects") ? "projects" : "mail";

  const mailNav = [
    { name: "Boîte de réception", href: "/dashboard/inbox", icon: Inbox },
    { name: "Analytiques", href: "/dashboard/analytics", icon: BarChart3 },
    { name: "Paramètres", href: "/dashboard/settings", icon: Settings },
  ];

  const projectsNav = [
    { name: "Vue d'ensemble", href: "/dashboard/projects/overview", icon: LayoutDashboard },
    { name: "Mes projets", href: "/dashboard/projects", icon: FolderKanban },
    { name: "Paramètres", href: "/dashboard/settings", icon: Settings },
  ];

  const navigation = space === "projects" ? projectsNav : mailNav;
  const spaceLabel = space === "projects" ? "ALIA Projets" : "ALIA Mail";

  const isActive = (href) => {
    // Exact match for /dashboard/projects so it doesn't always match overview
    if (href === "/dashboard/projects") {
      return location.pathname === "/dashboard/projects";
    }
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getPlanBadge = (plan) => {
    switch (plan) {
      case "starter": return { label: "Starter", color: "bg-slate-500" };
      case "pro": return { label: "Pro", color: "bg-blue-500" };
      case "business": return { label: "Business", color: "bg-purple-500" };
      default: return { label: "Essai", color: "bg-green-500" };
    }
  };

  const planInfo = getPlanBadge(user?.subscription_plan);

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-[#020617]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-slate-900/50 border-r border-slate-200 dark:border-white/10 transition-all duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${sidebarCollapsed ? 'w-20' : 'w-64'}`}
        data-testid="dashboard-sidebar"
      >
        {/* Logo (click → back to space selector) */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-white/10">
          {!sidebarCollapsed && (
            <Link to="/dashboard" className="flex items-center" data-testid="sidebar-logo-link" title="Changer d'espace">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>
          )}
          {sidebarCollapsed && (
            <Link to="/dashboard" className="flex items-center justify-center mx-auto" title="Changer d'espace" data-testid="sidebar-logo-link-collapsed">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </Link>
          )}
          <button 
            className="hidden lg:block p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <ChevronLeft className={`w-4 h-4 text-slate-500 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
          </button>
          <button
            className="lg:hidden p-2 -mr-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-3 sm:py-4">
          <nav className="space-y-1 px-3">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg transition-all min-h-[44px] ${
                  isActive(item.href)
                    ? 'bg-blue-600/10 text-blue-600 dark:text-blue-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-').replace(/'/g, '')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="font-medium text-sm sm:text-base">{item.name}</span>
                )}
              </Link>
            ))}
          </nav>
        </ScrollArea>

        {/* Plan Info */}
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-slate-200 dark:border-white/10">
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500 uppercase tracking-wide">Plan actuel</span>
                <span className={`px-2 py-0.5 rounded text-xs text-white ${planInfo.color}`}>
                  {planInfo.label}
                </span>
              </div>
              <Link 
                to="/dashboard/settings" 
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Gérer l&apos;abonnement
              </Link>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header */}
        <header className="h-14 sm:h-16 flex items-center justify-between px-3 sm:px-4 lg:px-6 bg-white dark:bg-slate-900/50 border-b border-slate-200 dark:border-white/10">
          <button
            className="lg:hidden p-2.5 -ml-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-slate-600 dark:text-slate-400" />
          </button>

          <Link to="/dashboard" className="lg:hidden flex items-center">
            <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
          </Link>

          <div className="hidden lg:block flex-1">
            {/* Breadcrumb or search could go here */}
          </div>

          <div className="flex items-center gap-1 sm:gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center"
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-slate-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 sm:gap-3 p-1 sm:p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-[40px]" data-testid="user-menu">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-blue-600 text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[150px]">{user?.email}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                  <User className="w-4 h-4 mr-2" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Abonnement
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
